import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView, TextInput, View } from 'react-native';

import { ActivityIndicator, Button, Modal, Text, useTheme } from '@/components/ui';
import { useAssociations } from '@/hooks/use-data';

export interface AssignAssociationModalProps {
	visible: boolean;
	/** The association exactly as the homeowner typed it — seeds the filter. */
	associationNameRaw?: string;
	currentAssociationId?: string | null;
	busy?: boolean;
	onDismiss: () => void;
	onAssign: (associationId: string | null) => void;
}

/**
 * Route a message to an association WITHOUT naming a lot (#411).
 *
 * The companion to `AssignLotModal`, for the case that one cannot serve: support can see
 * whose association a message belongs to but not which owner sent it. Before this, the
 * only way to get it off the portfolio-wide pile was to guess a lot — so nobody did, and
 * the PM who could have answered it in seconds never saw it.
 *
 * Filters the already-loaded association list IN MEMORY rather than round-tripping per
 * keystroke (CLAUDE.md "Stable Search Inputs"). The whole portfolio is ~50 rows; a
 * network query per keystroke would blank the list mid-type for no benefit.
 */
export const AssignAssociationModal: React.FC<AssignAssociationModalProps> = ({
	visible,
	associationNameRaw,
	currentAssociationId,
	busy = false,
	onDismiss,
	onAssign,
}) => {
	const theme = useTheme();
	const [query, setQuery] = useState('');

	// Re-seed each time it opens: the message may have changed underneath.
	useEffect(() => {
		if (visible) setQuery(associationNameRaw ?? '');
	}, [visible, associationNameRaw]);

	/**
	 * `{ limit: 500 }`, matching every other caller — NOT `{ enabled: visible }`.
	 *
	 * `useAssociations` does not forward `enabled` to useQuery; it puts the options into
	 * the cache key AND appends them as URL params. So that gated nothing, issued
	 * `GET /associations?enabled=false`, and gave the modal its own cache entry that
	 * flipped on open — a fresh fetch and a spinner every single time, which is exactly
	 * what the in-memory filtering above exists to avoid.
	 */
	const { data: associations, isLoading } = useAssociations({ limit: 500 });

	const matches = useMemo(() => {
		const all = associations?.data ?? [];
		const needle = query.trim().toLowerCase();
		const filtered = needle
			? all.filter((a) => (a.name ?? '').toLowerCase().includes(needle))
			: all;
		return filtered.slice(0, 50);
	}, [associations, query]);

	return (
		<Modal visible={visible} onDismiss={onDismiss} testID="assign-association-modal">
			<View style={{ padding: 20, minWidth: 380, maxWidth: 560 }}>
				<Text style={{ fontSize: 18, fontWeight: '700', marginBottom: 4 }}>Set association</Text>
				<Text style={{ color: theme.colors.onSurfaceVariant, marginBottom: 8 }}>
				Files this message under an association so its property manager can see it. The
				owner can still be assigned later.
				</Text>

				<TextInput
					value={query}
					onChangeText={setQuery}
					placeholder="Filter associations…"
					placeholderTextColor={theme.colors.onSurfaceVariant}
					testID="assign-association-search"
					style={{
						borderWidth: 1,
						borderColor: theme.colors.outline,
						borderRadius: 8,
						paddingHorizontal: 12,
						paddingVertical: 10,
						color: theme.colors.onSurface,
						marginBottom: 8,
					}}
				/>

				{isLoading ? (
					<View style={{ paddingVertical: 24, alignItems: 'center' }}><ActivityIndicator /></View>
				) : (
					<ScrollView style={{ maxHeight: 320 }} testID="assign-association-results">
						{matches.length === 0 ? (
							<Text style={{ color: theme.colors.onSurfaceVariant, paddingVertical: 16 }}>
							No association matches “{query}”.
							</Text>
						) : matches.map((association) => {
							const isCurrent = association.id === currentAssociationId;
							return (
								<Pressable
									key={association.id}
									onPress={() => onAssign(association.id)}
									disabled={busy}
									testID={`assign-association-${association.id}`}
									style={{
										flexDirection: 'row',
										alignItems: 'center',
										paddingVertical: 10,
										paddingHorizontal: 8,
										borderBottomWidth: 1,
										borderBottomColor: theme.colors.outlineVariant ?? theme.colors.outline,
										backgroundColor: isCurrent ? theme.colors.primaryContainer : 'transparent',
									}}
								>
									<View style={{ flex: 1, minWidth: 0 }}>
										<Text numberOfLines={1} style={{ fontWeight: '600' }}>{association.name}</Text>
										{association.city ? (
											<Text numberOfLines={1} style={{ fontSize: 12, color: theme.colors.onSurfaceVariant }}>
												{association.city}
											</Text>
										) : null}
									</View>
									{isCurrent ? (
										<Ionicons name="checkmark-circle" size={18} color={theme.colors.primary} />
									) : null}
								</Pressable>
							);
						})}
					</ScrollView>
				)}

				<View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
					{currentAssociationId ? (
					// Clearing must be possible: a wrong routing has to be correctable, not
					// merely replaceable with another wrong one.
						<Button mode="text" onPress={() => onAssign(null)} disabled={busy} testID="assign-association-clear">
						Clear
						</Button>
					) : null}
					<Button mode="outlined" onPress={onDismiss} disabled={busy}>Cancel</Button>
				</View>
			</View>
		</Modal>
	);
};

export default AssignAssociationModal;
